// @spec SCF-005
// API response and error type definitions

/**
 * Standard API error response structure from the backend.
 */
export interface ApiError {
  status: number;
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

/**
 * Custom error class for API client errors.
 *
 * Extends Error with additional properties for HTTP status,
 * error codes, and detailed error information.
 *
 * @example
 * try {
 *   await apiClient.get('/users');
 * } catch (error) {
 *   if (error instanceof ApiClientError) {
 *     console.log(error.status); // 404
 *     console.log(error.code);   // 'NOT_FOUND'
 *   }
 * }
 */
export class ApiClientError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, message: string, code?: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.status = status;
    this.code = code;
    this.details = details;

    // Maintains proper stack trace for where error was thrown (V8 only)
    if (typeof Error.captureStackTrace === 'function') {
      Error.captureStackTrace(this, ApiClientError);
    }
  }
}

/**
 * Standard paginated response structure.
 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Standard success response wrapper.
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
}
